import { Injector } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { AuthService, AuthenticationInfo, NavigationService } from '@app/core/services';
import { ClaimPermissions, DocumentSiteClaimPage, SiteClaimSection } from '@app/enums';

export interface ReadOnlyParameters {
	section: string;
	page: string;
	module: string;
}

const getIsReadOnly = ({ injector, params }: { injector: Injector; params?: ReadOnlyParameters }) => {
	if (!injector) {
		return true;
	}
	const authService = injector.get(AuthService);
	const navigationService = injector.get(NavigationService);
	let isReadOnly = true;
	if (params) {
		const { section, page, module } = params;
		isReadOnly = authService.isReadOnly(section, page, module);
	} else {
		isReadOnly = authService.isReadOnlyByLink(navigationService.route);
	}

	return isReadOnly;
};

/**
 * Component Decorator
 *
 * @example
 * 	@WithReadOnly()
 * 	@Component(){...}
 */
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const WithReadOnly =
	(params?: ReadOnlyParameters) =>
	(cmpType: any): any => {
		const originalFactory = cmpType.ɵfac;
		cmpType.ɵcmp.factory = (...args) => {
			const cmp = originalFactory(...args);
			cmp.isReadOnly = getIsReadOnly({
				injector: WithReadOnly.injector,
				params,
			});
			return cmp;
		};
		return cmpType;
	};

WithReadOnly.injector = null as Injector;

/**
 * Property Decorator
 *
 * @example
 * @Component(){
 *     @ReadOnly()
 *     isReadOnly: boolean;
 * }
 */
export const ReadOnly =
	(params?: ReadOnlyParameters) =>
	(target: Record<string, unknown> | any, property: PropertyKey): any => {
		const cacheKey = Symbol();
		// delete current property, redefine property
		if (delete target[property]) {
			Object.defineProperty(target, property, {
				get(this: any) {
					return this[cacheKey];
				},
				set() {
					this[cacheKey] = getIsReadOnly({
						injector: ReadOnly.injector,
						params,
					});
				},
			});
		}
	};

ReadOnly.injector = null as Injector;

const getIsGA = async ({ injector }: { injector: Injector }): Promise<boolean> => {
	if (!injector) {
		return false;
	}
	const authService = injector.get(AuthService);
	const authentication = await firstValueFrom<AuthenticationInfo>(authService.authentication$);
	return authentication?.isGA || false;
};

/**
 * Property Decorator
 *
 * @example
 * @Component(){
 *     @IsGlobalAdmin()
 *     isGlobalAdmin: boolean;
 * }
 */
export const IsGlobalAdmin = () => (target: Record<string, unknown> | any, property: PropertyKey) => {
	const cacheKey = Symbol();
	// delete current property, redefine property
	if (delete target[property]) {
		Object.defineProperty(target, property, {
			get(this: any) {
				return this[cacheKey] || false;
			},
			async set() {
				this[cacheKey] = await getIsGA({
					injector: IsGlobalAdmin.injector,
				});
			},
		});
	}
};

IsGlobalAdmin.injector = null as Injector;

export interface DocManagementParameters {
	section?: string | SiteClaimSection;
	module?: string;
	page: string | DocumentSiteClaimPage;
	deleteAccess: string;
	confidentialAccess: string;
}

export interface DocPermissions {
	permissionLevel: null | ClaimPermissions.ReadOnly | ClaimPermissions.ViewEdit;
	hasDeleteAccess: boolean;
	hasConfidentialAccess: boolean;
}

const getDocumentPermissions = ({ injector, params }: { injector: Injector; params?: DocManagementParameters }): DocPermissions => {
	const permissions: DocPermissions = {
		permissionLevel: null,
		hasDeleteAccess: false,
		hasConfidentialAccess: false,
	};

	if (!injector) {
		return permissions;
	}
	let { page, module, section } = params;
	const { deleteAccess, confidentialAccess } = params;

	const authService = injector.get(AuthService);
	const navigationService = injector.get(NavigationService);

	let isReadOnly = true;
	let isAccessible = false;

	// calculate section, module by the url
	// if the DocMgmt site claim has the same section and module, just define page
	const siteClaim = authService.siteClaimByLink(navigationService.route);
	if (siteClaim) {
		section ??= siteClaim.Section;
		module ??= siteClaim.Module;
		page ??= siteClaim.Page;
	}

	if (section && page && module) {
		isAccessible = authService.isAccessible(section, module, page);
		isReadOnly = authService.isReadOnly(section, page, module);
	}

	if (isAccessible) {
		permissions.permissionLevel = isReadOnly ? ClaimPermissions.ReadOnly : ClaimPermissions.ViewEdit;
	}

	if (deleteAccess) {
		permissions.hasDeleteAccess = authService.isFieldAccessible(deleteAccess);
	}

	if (confidentialAccess) {
		permissions.hasConfidentialAccess = authService.isFieldAccessible(confidentialAccess);
	}

	return permissions;
};

/**
 * Property Decorator
 *
 * @example
 * @Component(){
 *		@DocManagement({
 *			page: DocumentSiteClaims.ManageAssetDocMgmt,
 *			deleteAccess: RoleClaims.FixedAssetAssetDocMgmtDelete,
 *			confidentialAccess: RoleClaims.FixedAssetAssetDocMgmtConfidential,
 *		})
 *		docMgmt = { permissionLevel: null, hasDeleteAccess: false, hasConfidentialAccess: false };
 * }
 *
 * @example
 * @Component(){
 *		@DocManagement({
 *          section: 'fa',
 *          module: 'manage',
 *			page: DocumentSiteClaims.ManageAssetDocMgmt,
 *			deleteAccess: RoleClaims.FixedAssetAssetDocMgmtDelete,
 *			confidentialAccess: RoleClaims.FixedAssetAssetDocMgmtConfidential,
 *		})
 *		docMgmt = { permissionLevel: null, hasDeleteAccess: false, hasConfidentialAccess: false };
 * }
 */
export const DocManagement =
	(params: DocManagementParameters) =>
	(target: Record<string, unknown> | any, property: PropertyKey): any => {
		const cacheKey = Symbol();
		if (delete target[property]) {
			Object.defineProperty(target, property, {
				get(this: any) {
					return this[cacheKey];
				},
				set() {
					this[cacheKey] = getDocumentPermissions({ injector: DocManagement.injector, params });
				},
			});
		}
	};

DocManagement.injector = null as Injector;
